import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const PROD_HOST = 'learnwithtrust.com';

/**
 * CanonicalTag — keeps `<link rel="canonical">` in sync with the
 * current SPA route. The static stamp from `public/index.html` only
 * captures the initial pathname; when React-Router pushes a new URL
 * we need to rewrite the tag so Google never sees duplicate canonicals.
 *
 * Behaviour:
 *   • Production host  → upsert <link rel="canonical" href="https://learnwithtrust.com{path}">
 *   • Any other host   → leave the noindex meta injected by index.html alone.
 *                        Optionally strip any stale canonical so we never
 *                        accidentally tell Google "this preview URL is real".
 *
 * Strip query strings and hashes from the canonical — UTM params should
 * never split SEO equity.
 */
export default function CanonicalTag() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const host   = window.location.hostname || '';
    const isProd = host === PROD_HOST || host === 'www.' + PROD_HOST;

    // Always remove any prior canonical link before deciding what to do.
    document.head.querySelectorAll('link[rel="canonical"]').forEach((n) => n.remove());

    if (!isProd) return;

    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', `https://${PROD_HOST}${pathname || '/'}`);
    document.head.appendChild(link);
  }, [pathname]);

  return null;
}
